import { FC, ReactNode, useCallback } from 'react'

import { useSuspenseQuery } from '@tanstack/react-query'

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  Cluster,
  GridItem,
  LabelValue,
  Stack,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@forto-dev/phoenix-ui-core'
import { Grid } from '@forto-dev/phoenix-ui-core/common'
import { StatusIcon } from '@forto-dev/phoenix-ui-core/ship'

import { useLocalisation } from '@/utils/localisation'

import {
  availableNotificationPreferencesQueryOptions,
  currentNotificationPreferencesQueryOptions,
} from '../api/preferences.service'
import { useNotificationPreferenceUpdate } from '../api/useNotificationPreferenceUpdate'
import {
  AvailableNotificationPreference,
  NOTIFICATION_PREFERENCE_SUB_CATEGORIES,
  NotificationChannel,
  NotificationPreference,
  NotificationPreferenceType,
  NotificationSection,
} from '../domain/settings'

const getIconForNotificationPreferenceType = (type: NotificationPreferenceType): ReactNode => {
  switch (type) {
    case 'update':
      return <StatusIcon icon="Refresh" color="muted" />
    case 'reassurance':
      return <StatusIcon icon="Check" color="positive" />
    case 'deviationRisk':
      return <StatusIcon icon="WarningPlain" color="warning" />
    case 'deviation':
      return <StatusIcon icon="WarningPlain" color="negative" />
  }
}

/**
 * The main use case here is so that when instant or digest emails are enabled, web is also enabled, and if web is disabled, instant and digest emails are disabled too.
 * This is because we expect there to be a corresponding web notification for every email notification.
 * The exception is if the preference does not allow web notifications, in which case web notifications are disabled. The way that works is the backend includes links to the entity instead of the notification.
 */
const createPreferenceUpdate = (
  reference: string,
  updates: Partial<NotificationPreference>,
  preference: AvailableNotificationPreference,
  currentPreference?: NotificationPreference
): NotificationPreference => {
  const base = {
    reference,
    web: currentPreference?.web ?? preference.default?.web ?? false,
    instantEmail: currentPreference?.instantEmail ?? preference.default?.instantEmail ?? false,
    digestEmail: currentPreference?.digestEmail ?? preference.default?.digestEmail ?? false,
    ...updates,
  }
  // If web is being enabled and any email notification is active, keep web enabled
  if (isChannelAllowed(preference, 'web')) {
    base.web = base.instantEmail || base.digestEmail || base.web
  } else {
    base.web = false
  }

  // If web is being disabled, disable email notifications too
  if (updates.web === false) {
    base.instantEmail = false
    base.digestEmail = false
    base.web = false
  }
  return base
}
const isChannelAllowed = (preference: AvailableNotificationPreference, channel: NotificationChannel) => {
  const visibleChannels = preference?.visibleChannels
  return visibleChannels ? visibleChannels.indexOf(channel) > -1 : true
}
const CHANNEL_OPTIONS = [
  { value: 'web', label: 'notification-settings.preferences.actions.web' },
  { value: 'instantEmail', label: 'notification-settings.preferences.actions.instant' },
  { value: 'digestEmail', label: 'notification-settings.preferences.actions.digest' },
] as const

const NotificationPreferenceSelector: FC<{
  preference: AvailableNotificationPreference
  currentPreference?: NotificationPreference
  onUpdate: (updates: NotificationPreference[]) => Promise<void>
  keyPrefix: string
}> = ({ preference, currentPreference, onUpdate, keyPrefix }) => {
  const { translate } = useLocalisation()

  const handleCheckboxChange = useCallback(
    async (channel: NotificationChannel, checked: boolean) => {
      const updates = preference.references.map((reference) => {
        const update = createPreferenceUpdate(reference, { [channel]: checked }, preference, currentPreference)
        return update
      })
      await onUpdate(updates)
    },
    [preference, currentPreference, onUpdate]
  )

  return (
    <Grid gap={16} col={3}>
      {CHANNEL_OPTIONS.map(({ value, label }) => {
        const channel = value as NotificationChannel
        if (!isChannelAllowed(preference, channel)) {
          return <Stack key={value} />
        }

        return (
          <Checkbox
            key={value}
            id={`${keyPrefix}-${value}`}
            label={translate(label)}
            checked={currentPreference?.[channel] ?? preference.default?.[channel]}
            onCheckedChange={(checked) => handleCheckboxChange(channel, Boolean(checked))}
          />
        )
      })}
    </Grid>
  )
}
export const Preferences: FC = () => {
  const { translate } = useLocalisation()
  const { mutateAsync, isPending } = useNotificationPreferenceUpdate()
  const { data: availableNotificationPreferences } = useSuspenseQuery(availableNotificationPreferencesQueryOptions())
  const { data: currentNotificationPreferences } = useSuspenseQuery(currentNotificationPreferencesQueryOptions())
  const updateBulkPreferences = useCallback(
    async (section: NotificationSection, channel: NotificationChannel, checked: boolean) => {
      const updates = section.preferences
        .map((availablePreference) =>
          availablePreference.references.map((reference) => {
            const currentPreference = currentNotificationPreferences.preferences.find(
              (preference) => preference.reference === reference
            )
            return createPreferenceUpdate(reference, { [channel]: checked }, availablePreference, currentPreference)
          })
        )
        .flat()

      await mutateAsync({ preferences: updates })
    },
    [currentNotificationPreferences.preferences, mutateAsync]
  )
  const disableEditing = isPending
  return (
    <>
      <Stack gap={16}>
        <Card>
          <CardHeader>
            <CardTitle>{translate('notification-settings.preferences.heading')}</CardTitle>
            <CardDescription>{translate('notification-settings.preferences.intro')}</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="preTransport">
              <Stack gap={16}>
                <TabsList>
                  {availableNotificationPreferences.sections.map((section) => (
                    <TabsTrigger key={section.category} value={section.category}>
                      {translate(`notification-settings.preferences.categories.${section.category}`)}
                    </TabsTrigger>
                  ))}
                </TabsList>
                <Stack>
                  {availableNotificationPreferences.sections.map((section) => {
                    const categories = [...NOTIFICATION_PREFERENCE_SUB_CATEGORIES, 'none'] as const

                    return (
                      <TabsContent key={section.category} value={section.category}>
                        <Stack gap={16}>
                          <fieldset disabled={disableEditing}>
                            <Cluster gap={16} x="end">
                              <Switch
                                id={`${section.category}-all-web`}
                                label={translate('notification-settings.preferences.actions.web_all')}
                                checked={section.preferences.every(
                                  (preference) =>
                                    !isChannelAllowed(preference, 'web') ||
                                    preference.references.every(
                                      (reference) =>
                                        currentNotificationPreferences.preferences.find(
                                          (currentPreference) => currentPreference.reference === reference
                                        )?.web || preference.default?.web
                                    )
                                )}
                                onCheckedChange={async (checked) => {
                                  await updateBulkPreferences(section, 'web', checked)
                                }}
                              />
                              <Switch
                                id={`${section.category}-all-instant`}
                                label={translate('notification-settings.preferences.actions.instant_all')}
                                checked={section.preferences.every((preference) =>
                                  preference.references.every(
                                    (reference) =>
                                      !isChannelAllowed(preference, 'instantEmail') ||
                                      currentNotificationPreferences.preferences.find(
                                        (currentPreference) => currentPreference.reference === reference
                                      )?.instantEmail ||
                                      preference.default?.instantEmail
                                  )
                                )}
                                onCheckedChange={async (checked) => {
                                  await updateBulkPreferences(section, 'instantEmail', checked)
                                }}
                              />
                              <Switch
                                id={`${section.category}-all-digest`}
                                label={translate('notification-settings.preferences.actions.digest_all')}
                                checked={section.preferences.every(
                                  (preference) =>
                                    !isChannelAllowed(preference, 'digestEmail') ||
                                    preference.references.every(
                                      (reference) =>
                                        currentNotificationPreferences.preferences.find(
                                          (currentPreference) => currentPreference.reference === reference
                                        )?.digestEmail || preference.default?.digestEmail
                                    )
                                )}
                                onCheckedChange={async (checked) => {
                                  await updateBulkPreferences(section, 'digestEmail', checked)
                                }}
                              />
                            </Cluster>
                          </fieldset>
                          <Accordion type="multiple">
                            {categories.map((category) => {
                              const preferences = section.preferences.filter((preference) =>
                                category === 'none' ? !preference.subCategory : preference.subCategory === category
                              )

                              return preferences.length ? (
                                <AccordionItem key={category} value={category}>
                                  <AccordionTrigger>
                                    {translate(`notification-settings.preferences.subcategories.${category}`)}
                                  </AccordionTrigger>
                                  <AccordionContent>
                                    {/* fieldset is within Accordion otherwise it gets disabled too */}
                                    <fieldset disabled={disableEditing}>
                                      <Stack gap={16}>
                                        {preferences.map((preference) => {
                                          const key = preference.references.join('_')
                                          const currentPreference = currentNotificationPreferences.preferences.find(
                                            (currentPreference) =>
                                              currentPreference.reference === preference.references[0]
                                          )

                                          return (
                                            <Grid gap={16} col={12} key={key} breakpoint="md">
                                              <GridItem span={7}>
                                                <Cluster gap={8} y="center">
                                                  {getIconForNotificationPreferenceType(preference.type)}
                                                  <LabelValue
                                                    label={translate(
                                                      // @ts-expect-error Dynamic translation key
                                                      `notification-settings.preferences.preferences.${preference.title}`
                                                    )}
                                                    value={translate(
                                                      // @ts-expect-error Dynamic translation key
                                                      `notification-settings.preferences.preferences.${preference.subtitle}`
                                                    )}
                                                    size="xs"
                                                  />
                                                </Cluster>
                                              </GridItem>
                                              <GridItem span={5}>
                                                <Cluster x="end">
                                                  <NotificationPreferenceSelector
                                                    preference={preference}
                                                    currentPreference={currentPreference}
                                                    onUpdate={(preferences) => mutateAsync({ preferences })}
                                                    keyPrefix={key}
                                                  />
                                                </Cluster>
                                              </GridItem>
                                            </Grid>
                                          )
                                        })}
                                      </Stack>
                                    </fieldset>
                                  </AccordionContent>
                                </AccordionItem>
                              ) : null
                            })}
                          </Accordion>
                        </Stack>
                      </TabsContent>
                    )
                  })}
                </Stack>
              </Stack>
            </Tabs>
          </CardContent>
        </Card>
      </Stack>
    </>
  )
}
